import React, { useState, useEffect, useCallback, useRef } from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, AreaChart, Area, BarChart, Bar } from 'recharts';
import { 
  Activity, Users, BarChart3, Database, Settings, Play, Square, RefreshCw, 
  Trash2, Edit3, Shield, Zap, Globe, Search, TrendingUp, TrendingDown,
  CheckCircle, AlertTriangle, Clock, Cpu, HardDrive, DollarSign
} from 'lucide-react';
import * as THREE from 'three';
import * as d3 from 'd3';
import axios from 'axios';
import { useAuth } from '../utils/auth';
import NotAuthorized from './NotAuthorized';

const UnifiedAdminDashboard = () => {
  const { user } = useAuth();
  const [metrics, setMetrics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [historicalData, setHistoricalData] = useState([]);
  const [selectedTimeRange, setSelectedTimeRange] = useState('1h');
  const [activeTab, setActiveTab] = useState('overview');
  const threeContainerRef = useRef(null);
  const networkContainerRef = useRef(null);

  // Admin-only check
  if (user?.role !== 'admin') {
    return <NotAuthorized />;
  }

  // Simulate real metrics data
  const generateMetrics = useCallback(() => {
    return {
      system: {
        cpu_percent: 45 + Math.random() * 30,
        memory_percent: 62 + Math.random() * 20,
        disk_usage: 78 + Math.random() * 10,
        network_io: Math.random() * 1000,
        load_average: 1.2 + Math.random() * 0.8
      },
      database: {
        connections: 45 + Math.floor(Math.random() * 20),
        active_connections: 12 + Math.floor(Math.random() * 8),
        total_users: 12847 + Math.floor(Math.random() * 100),
        query_time_avg: 15 + Math.random() * 10,
        status: 'online'
      },
      application: {
        requests_per_minute: 234 + Math.floor(Math.random() * 100),
        response_time_avg: 145 + Math.random() * 50,
        error_rate: Math.random() * 0.05,
        active_sessions: 156 + Math.floor(Math.random() * 50)
      },
      training: {
        is_running: false,
        current_accuracy: 96.3,
        last_training: '2024-08-21 18:56:37',
        total_epochs_completed: 15,
        api_success_rate: 100.0
      },
      ai_agents: [
        { name: 'Design Agent', load: 45 + Math.random() * 40, accuracy: 94.2, status: 'active' },
        { name: 'Performance Agent', load: 62 + Math.random() * 30, accuracy: 91.8, status: 'active' },
        { name: 'SEO Agent', load: 38 + Math.random() * 35, accuracy: 96.1, status: 'active' },
        { name: 'Copy Agent', load: 55 + Math.random() * 25, accuracy: 89.3, status: 'active' },
        { name: 'Business Agent', load: 71 + Math.random() * 20, accuracy: 92.7, status: 'active' }
      ],
      timestamp: Date.now()
    };
  }, []);

  // Fetch metrics
  const fetchMetrics = useCallback(async () => {
    try {
      const data = generateMetrics();
      setMetrics(data);
      setError(null);
      
      // Add to historical data
      const timestamp = new Date().toLocaleTimeString();
      setHistoricalData(prev => {
        const newData = [...prev, {
          time: timestamp,
          ...data.system,
          ...data.application,
          dbConnections: data.database.connections
        }];
        return newData.slice(-50);
      });
      
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [generateMetrics]);

  // Auto-refresh effect
  useEffect(() => {
    fetchMetrics();
    
    if (autoRefresh) {
      const interval = setInterval(fetchMetrics, 5000);
      return () => clearInterval(interval);
    }
  }, [fetchMetrics, autoRefresh]);

  // User Management Component
  const UserManagement = () => {
    const [users, setUsers] = useState([]);
    const [userLoading, setUserLoading] = useState(false);
    const [filter, setFilter] = useState('all');

    useEffect(() => {
      fetchUsers();
    }, [filter]);

    const fetchUsers = async () => {
      setUserLoading(true);
      try {
        const response = await axios.get('/admin/users', {
          headers: { Authorization: `Bearer ${localStorage.getItem('coderated_auth_token')}` },
          params: { role: filter === 'all' ? undefined : filter }
        });
        setUsers(response.data.users || []);
      } catch (error) {
        console.error('Failed to fetch users:', error);
        // Mock data fallback
        setUsers([
          { id: 1, email: 'admin@coderated.com', role: 'admin', created_at: '2024-01-01', tier: 'enterprise' },
          { id: 2, email: 'user@example.com', role: 'user', created_at: '2024-01-15', tier: 'professional' }
        ]);
      } finally {
        setUserLoading(false);
      }
    };

    const updateUserRole = async (userId, newRole) => {
      try {
        await axios.post(`/admin/users/${userId}/role`, { role: newRole }, {
          headers: { Authorization: `Bearer ${localStorage.getItem('coderated_auth_token')}` }
        });
        await fetchUsers();
      } catch (error) {
        console.error('Failed to update user role:', error);
        alert('Failed to update user role');
      }
    };

    return (
      <div className="bg-slate-900/50 backdrop-blur-xl border border-slate-700/50 rounded-2xl p-6">
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-xl font-bold text-white">User Management</h3>
          <button
            onClick={fetchUsers}
            className="flex items-center gap-2 px-3 py-2 text-sm bg-slate-800/50 hover:bg-slate-700/50 text-slate-300 rounded-lg transition-colors"
          >
            <RefreshCw className="w-4 h-4" />
            Refresh
          </button>
        </div>
        
        <div className="flex gap-2 mb-4">
          {['all', 'admin', 'user'].map(roleFilter => (
            <button
              key={roleFilter}
              onClick={() => setFilter(roleFilter)}
              className={`px-3 py-1 text-sm rounded-lg transition-colors ${
                filter === roleFilter
                  ? 'bg-blue-600 text-white'
                  : 'bg-slate-800/50 text-slate-400 hover:text-white'
              }`}
            >
              {roleFilter.charAt(0).toUpperCase() + roleFilter.slice(1)}
            </button>
          ))}
        </div>

        {userLoading ? (
          <div className="text-center py-8">
            <div className="animate-spin w-6 h-6 border-2 border-blue-500 border-t-transparent rounded-full mx-auto"></div>
            <p className="text-slate-400 mt-2">Loading users...</p>
          </div>
        ) : (
          <div className="space-y-3">
            {users.map(user => (
              <div key={user.id} className="flex items-center justify-between p-4 bg-slate-800/30 rounded-lg">
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 bg-blue-600 rounded-full flex items-center justify-center text-white text-sm font-medium">
                    {user.email?.[0]?.toUpperCase() || 'U'}
                  </div>
                  <div>
                    <div className="font-medium text-white">{user.email}</div>
                    <div className="text-sm text-slate-400">
                      Joined: {new Date(user.created_at).toLocaleDateString()}
                    </div>
                  </div>
                </div>

                <div className="flex items-center gap-3">
                  <span className={`px-2 py-1 text-xs rounded-full ${
                    user.role === 'admin' ? 'bg-red-500/20 text-red-300' : 'bg-blue-500/20 text-blue-300'
                  }`}>
                    {user.role?.toUpperCase() || 'USER'}
                  </span>
                  
                  {user.role !== 'admin' && (
                    <select
                      value={user.role}
                      onChange={(e) => updateUserRole(user.id, e.target.value)}
                      className="text-sm bg-slate-800/50 border border-slate-600/50 rounded px-2 py-1 text-white"
                    >
                      <option value="user">User</option>
                      <option value="admin">Admin</option>
                    </select>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  };

  // AI Training Component
  const AITraining = () => {
    const [training, setTraining] = useState({ isRunning: false, progress: 0, logs: [] });
    const [config, setConfig] = useState({
      epochs: 15,
      batchSize: 6,
      learningRate: 0.0001
    });

    const startTraining = async () => {
      try {
        const response = await axios.post('/admin/training/start', config, {
          headers: { Authorization: `Bearer ${localStorage.getItem('coderated_auth_token')}` }
        });
        
        setTraining(prev => ({ ...prev, isRunning: true, logs: ['Training started...'] }));
        
        // Poll for status
        const interval = setInterval(async () => {
          try {
            const statusResponse = await axios.get('/admin/training/status', {
              headers: { Authorization: `Bearer ${localStorage.getItem('coderated_auth_token')}` }
            });
            
            const status = statusResponse.data;
            setTraining(prev => ({
              ...prev,
              isRunning: status.is_training,
              progress: status.current_job?.progress || 0
            }));
            
            if (!status.is_training) {
              clearInterval(interval);
            }
          } catch (error) {
            console.error('Failed to get training status:', error);
          }
        }, 2000);
        
      } catch (error) {
        console.error('Failed to start training:', error);
        alert('Failed to start training: ' + (error.response?.data?.detail || error.message));
      }
    };

    const stopTraining = async () => {
      try {
        await axios.post('/admin/training/stop', {}, {
          headers: { Authorization: `Bearer ${localStorage.getItem('coderated_auth_token')}` }
        });
        setTraining(prev => ({ ...prev, isRunning: false }));
      } catch (error) {
        console.error('Failed to stop training:', error);
      }
    };

    return (
      <div className="bg-slate-900/50 backdrop-blur-xl border border-slate-700/50 rounded-2xl p-6">
        <h3 className="text-xl font-bold text-white mb-6">AI Training Control</h3>

        <div className="space-y-6">
          {/* Training Status */}
          <div className="bg-slate-800/30 rounded-lg p-4">
            <div className="flex items-center justify-between mb-3">
              <span className="text-slate-300">Training Status</span>
              <div className={`flex items-center gap-2 ${training.isRunning ? 'text-green-400' : 'text-slate-400'}`}>
                <div className={`w-2 h-2 rounded-full ${training.isRunning ? 'bg-green-400 animate-pulse' : 'bg-slate-400'}`}></div>
                <span className="text-sm">{training.isRunning ? 'Running' : 'Idle'}</span>
              </div>
            </div>
            
            {training.isRunning && (
              <div className="w-full bg-slate-700/50 rounded-full h-2">
                <div 
                  className="h-2 rounded-full bg-gradient-to-r from-blue-500 to-green-500 transition-all duration-1000"
                  style={{ width: `${training.progress}%` }}
                ></div>
              </div>
            )}
          </div>

          {/* Last Training Results */}
          <div className="bg-slate-800/30 rounded-lg p-4">
            <h4 className="text-white font-medium mb-3">Last Training Results</h4>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <div className="text-slate-400 text-sm">Final Accuracy</div>
                <div className="text-white font-medium">{metrics?.training?.current_accuracy}%</div>
              </div>
              <div>
                <div className="text-slate-400 text-sm">API Success Rate</div>
                <div className="text-white font-medium">{metrics?.training?.api_success_rate}%</div>
              </div>
              <div>
                <div className="text-slate-400 text-sm">Epochs Completed</div>
                <div className="text-white font-medium">{metrics?.training?.total_epochs_completed}</div>
              </div>
              <div>
                <div className="text-slate-400 text-sm">Last Run</div>
                <div className="text-white font-medium">
                  {new Date(metrics?.training?.last_training).toLocaleDateString()}
                </div>
              </div>
            </div>
          </div>

          {/* Training Configuration */}
          <div className="grid grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate-300 mb-1">Epochs</label>
              <input
                type="number"
                value={config.epochs}
                onChange={(e) => setConfig({...config, epochs: parseInt(e.target.value)})}
                disabled={training.isRunning}
                className="w-full px-3 py-2 bg-slate-800/50 border border-slate-600/50 rounded text-white focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-300 mb-1">Batch Size</label>
              <input
                type="number"
                value={config.batchSize}
                onChange={(e) => setConfig({...config, batchSize: parseInt(e.target.value)})}
                disabled={training.isRunning}
                className="w-full px-3 py-2 bg-slate-800/50 border border-slate-600/50 rounded text-white focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-300 mb-1">Learning Rate</label>
              <input
                type="number"
                step="0.0001"
                value={config.learningRate}
                onChange={(e) => setConfig({...config, learningRate: parseFloat(e.target.value)})}
                disabled={training.isRunning}
                className="w-full px-3 py-2 bg-slate-800/50 border border-slate-600/50 rounded text-white focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
          </div>

          {/* Training Controls */}
          <div className="flex gap-3">
            <button
              onClick={startTraining}
              disabled={training.isRunning}
              className="flex items-center gap-2 px-6 py-3 bg-blue-600 hover:bg-blue-700 disabled:bg-slate-600 disabled:cursor-not-allowed text-white rounded-lg font-medium transition-colors"
            >
              <Play className="w-4 h-4" />
              Start Training
            </button>
            
            <button 
              onClick={stopTraining}
              disabled={!training.isRunning}
              className="flex items-center gap-2 px-6 py-3 bg-red-600 hover:bg-red-700 disabled:bg-slate-600 disabled:cursor-not-allowed text-white rounded-lg font-medium transition-colors"
            >
              <Square className="w-4 h-4" />
              Stop Training
            </button>
          </div>
        </div>
      </div>
    );
  };

  // Metric Card Component
  const MetricCard = ({ title, value, unit, trend, color, icon: Icon, status }) => (
    <div className="bg-slate-900/50 backdrop-blur-xl border border-slate-700/50 rounded-xl p-6 hover:border-slate-600/50 transition-all duration-300">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center space-x-3">
          <div className={`w-10 h-10 ${color} rounded-xl flex items-center justify-center`}>
            <Icon className="w-5 h-5 text-white" />
          </div>
          <div>
            <h3 className="text-slate-300 font-medium text-sm">{title}</h3>
            {status && (
              <div className="flex items-center space-x-1 mt-1">
                <div className={`w-2 h-2 rounded-full ${status === 'healthy' ? 'bg-green-400' : status === 'warning' ? 'bg-yellow-400' : 'bg-red-400'} animate-pulse`}></div>
                <span className={`text-xs ${status === 'healthy' ? 'text-green-400' : status === 'warning' ? 'text-yellow-400' : 'text-red-400'}`}>
                  {status.toUpperCase()}
                </span>
              </div>
            )}
          </div>
        </div>
        {trend && (
          <div className={`flex items-center text-sm ${trend > 0 ? 'text-green-400' : 'text-red-400'}`}>
            {trend > 0 ? <TrendingUp className="w-4 h-4 mr-1" /> : <TrendingDown className="w-4 h-4 mr-1" />}
            {Math.abs(trend)}%
          </div>
        )}
      </div>
      
      <div className="flex items-baseline space-x-2">
        <span className="text-3xl font-bold text-white">
          {typeof value === 'number' ? value.toFixed(0) : value}
        </span>
        {unit && <span className="text-slate-400 text-lg">{unit}</span>}
      </div>
    </div>
  );

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-slate-900/95 backdrop-blur-xl border border-slate-700/50 rounded-xl p-4 shadow-2xl">
          <p className="text-slate-300 text-sm mb-2">{label}</p>
          {payload.map((entry, index) => (
            <p key={index} className="text-white font-medium" style={{ color: entry.color }}>
              {entry.name}: {entry.value}
            </p>
          ))}
        </div>
      );
    }
    return null;
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-950 via-slate-900 to-slate-800">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-blue-500/30 border-t-blue-500 rounded-full animate-spin mx-auto mb-4"></div>
          <h3 className="text-2xl font-bold text-white mb-2">Loading Admin Dashboard</h3>
          <p className="text-slate-400">Initializing system metrics</p>
        </div>
      </div>
    );
  }

  const tabs = [
    { id: 'overview', label: 'System Overview', icon: BarChart3 },
    { id: 'users', label: 'User Management', icon: Users },
    { id: 'training', label: 'AI Training', icon: Zap },
    { id: 'agents', label: 'AI Agents', icon: Activity }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-slate-800 pt-24 px-4">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="relative mb-8">
          <div className="bg-slate-900/50 backdrop-blur-xl border border-slate-700/50 rounded-3xl p-8">
            <div className="flex justify-between items-center">
              <div>
                <h1 className="text-4xl font-bold text-white mb-2">
                  Admin Dashboard
                </h1>
                <p className="text-slate-400 text-xl">System monitoring and management</p>
                <div className="flex items-center space-x-4 mt-4">
                  <div className="flex items-center space-x-2">
                    <div className="w-3 h-3 bg-green-400 rounded-full animate-pulse"></div>
                    <span className="text-green-400 font-medium">All Systems Operational</span>
                  </div>
                  <div className="text-slate-500">•</div>
                  <div className="text-slate-400">Last updated: {new Date().toLocaleTimeString()}</div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Tab Navigation */}
        <div className="mb-8">
          <div className="bg-slate-900/50 backdrop-blur-xl border border-slate-700/50 rounded-2xl p-2">
            <div className="flex space-x-2">
              {tabs.map(tab => {
                const IconComponent = tab.icon;
                return (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={`flex items-center space-x-2 px-6 py-3 rounded-xl font-medium transition-all duration-300 ${
                      activeTab === tab.id
                        ? 'bg-blue-600 text-white shadow-lg'
                        : 'text-slate-400 hover:text-white hover:bg-slate-800/50'
                    }`}
                  >
                    <IconComponent className="w-5 h-5" />
                    <span>{tab.label}</span>
                  </button>
                );
              })}
            </div>
          </div>
        </div>

        {/* Tab Content */}
        {activeTab === 'overview' && (
          <div className="space-y-8">
            {/* Key Metrics Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              <MetricCard
                title="CPU Usage"
                value={metrics?.system?.cpu_percent}
                unit="%"
                color="bg-blue-600"
                icon={Cpu}
                trend={-1.2}
              />
              <MetricCard
                title="Memory Usage"
                value={metrics?.system?.memory_percent}
                unit="%"
                color="bg-green-600"
                icon={HardDrive}
                trend={0.8}
              />
              <MetricCard
                title="Active Users"
                value={metrics?.database?.total_users}
                color="bg-purple-600"
                icon={Users}
                trend={5.2}
              />
              <MetricCard
                title="Active Sessions"
                value={metrics?.application?.active_sessions}
                color="bg-orange-600"
                icon={Globe}
                trend={2.1}
              />
            </div>

            {/* Charts */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              <div className="bg-slate-900/50 backdrop-blur-xl border border-slate-700/50 rounded-2xl p-6">
                <h3 className="text-xl font-bold text-white mb-4">System Performance</h3>
                <ResponsiveContainer width="100%" height={300}>
                  <AreaChart data={historicalData}>
                    <defs>
                      <linearGradient id="cpuGradient" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.8}/>
                        <stop offset="95%" stopColor="#3b82f6" stopOpacity={0.1}/>
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                    <XAxis dataKey="time" stroke="#9CA3AF" />
                    <YAxis stroke="#9CA3AF" />
                    <Tooltip content={<CustomTooltip />} />
                    <Area type="monotone" dataKey="cpu_percent" stroke="#3b82f6" fill="url(#cpuGradient)" strokeWidth={2} />
                  </AreaChart>
                </ResponsiveContainer>
              </div>

              <div className="bg-slate-900/50 backdrop-blur-xl border border-slate-700/50 rounded-2xl p-6">
                <h3 className="text-xl font-bold text-white mb-4">Response Times</h3>
                <ResponsiveContainer width="100%" height={300}>
                  <LineChart data={historicalData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                    <XAxis dataKey="time" stroke="#9CA3AF" />
                    <YAxis stroke="#9CA3AF" />
                    <Tooltip content={<CustomTooltip />} />
                    <Line type="monotone" dataKey="response_time_avg" stroke="#10b981" strokeWidth={3} dot={false} />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'users' && <UserManagement />}
        {activeTab === 'training' && <AITraining />}

        {activeTab === 'agents' && (
          <div className="space-y-8">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {metrics?.ai_agents?.map((agent, index) => (
                <div key={index} className="bg-slate-900/50 backdrop-blur-xl border border-slate-700/50 rounded-2xl p-6">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-white font-bold">{agent.name}</h3>
                    <div className="flex items-center space-x-2">
                      <div className="w-3 h-3 bg-green-400 rounded-full animate-pulse"></div>
                      <span className="text-green-400 text-sm font-medium">ACTIVE</span>
                    </div>
                  </div>
                  
                  <div className="space-y-4">
                    <div>
                      <div className="flex justify-between text-sm mb-2">
                        <span className="text-slate-400">CPU Load</span>
                        <span className="text-white">{agent.load.toFixed(1)}%</span>
                      </div>
                      <div className="w-full bg-slate-700/50 rounded-full h-2">
                        <div 
                          className="h-2 rounded-full bg-blue-500 transition-all duration-1000"
                          style={{ width: `${agent.load}%` }}
                        ></div>
                      </div>
                    </div>
                    
                    <div>
                      <div className="flex justify-between text-sm mb-2">
                        <span className="text-slate-400">Accuracy</span>
                        <span className="text-white">{agent.accuracy}%</span>
                      </div>
                      <div className="w-full bg-slate-700/50 rounded-full h-2">
                        <div 
                          className="h-2 rounded-full bg-green-500 transition-all duration-1000"
                          style={{ width: `${agent.accuracy}%` }}
                        ></div>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default UnifiedAdminDashboard;